import * as React from 'react';
import cx from 'classnames';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { State } from '../redux/state';
import * as selectors from '../redux/selectors';
import { withLocalization, LProps } from '../utils/wrappers';
import ScoreboardCampuses from '../shared/ScoreboardCampuses';
import ScoreboardTeams from '../shared/ScoreboardTeams';
import ScoreboardParticipants from '../shared/ScoreboardParticipants';
import { Competition, CompetitionConfig, CompetitionEntityScore, CompetitionUserScore } from '../userApi';
import ScoreboardHealthSystems from './ScoreboardHealthSystems';
import ScoreboardPointsTrend from './ScoreboardPointsTrend';
import { chartViewForScoreboardTab, ScoreboardTab } from './scoreboardChartView';
import { makeCompetitionUserScoresPath } from '../utils/routerHelper';
import { campusesLabelFor, teamsLabelFor, isHiddenFromScoreboard } from '../utils/competitionLabels';

interface Props {
  topTeamScores: CompetitionEntityScore[];
  topUserScores: CompetitionUserScore[];
  competitionID: number;
  competition: Competition;
  competitionConfig: CompetitionConfig;
}

enum ScoreboardView {
  Campuses = ScoreboardTab.Campuses,
  Participants = ScoreboardTab.Participants,
  Teams = ScoreboardTab.Teams,
  HealthSystems = ScoreboardTab.HealthSystems
}

interface ScoreboardState {
  scoreboardView: ScoreboardView;
}

class Scoreboard extends React.Component<LProps<Props>, ScoreboardState> {
  constructor(props: LProps<Props>) {
    super(props);
    const disabled = (props.competitionConfig && props.competitionConfig.scoreBoardCampusDisabled) || false;
    this.state = {
      scoreboardView: disabled ? ScoreboardView.Teams : ScoreboardView.Campuses
    };
  }

  public render() {
    const { scoreboardView } = this.state;
    const { t, topTeamScores, topUserScores, competitionID, competition, competitionConfig } = this.props;
    const scoreBoardCampusDisabled = (competitionConfig && competitionConfig.scoreBoardCampusDisabled) || false;
    const labeledCompetition = { config: competitionConfig } as Competition;
    const teamsLabel = teamsLabelFor(labeledCompetition, t('Teams'));
    const campusesLabel = campusesLabelFor(labeledCompetition, t('Campuses'));
    return (
      <div className="cc__scoreboard">
        <div className="cc__scoreboard__header">
          <div className={'cc__darktext'}>{t('Scoreboard')}</div>
          <div className={cx('cc__scoreboard__header_tabs')}>
             {!scoreBoardCampusDisabled &&
              <div
                role="button"
                className={cx('cc__scoreboard__header_tab', { active: scoreboardView === ScoreboardView.Campuses })}
                onClick={() => this.setState({ scoreboardView: ScoreboardView.Campuses })}
              >{campusesLabel}</div>
            }
             <div
              role="button"
              className={cx('cc__scoreboard__header_tab', { active: scoreboardView === ScoreboardView.Teams })}
              onClick={() => this.setState({ scoreboardView: ScoreboardView.Teams })}
            >{teamsLabel}</div>
            <div
              role="button"
              className={cx('cc__scoreboard__header_tab', { active: scoreboardView === ScoreboardView.Participants })}
              onClick={() => this.setState({ scoreboardView: ScoreboardView.Participants })}
            >{t('Participants')}</div>
          </div>
        </div>
        {scoreboardView === ScoreboardView.Campuses && <ScoreboardCampuses compact={true} />}
        {scoreboardView === ScoreboardView.Teams &&
          <ScoreboardTeams
            compact={true}
            pointsOnly={true}
            limit={25}
            teamScores={topTeamScores}
          />
        }
        {scoreboardView === ScoreboardView.Participants &&
          <ScoreboardParticipants
            viewAll={makeCompetitionUserScoresPath(competitionID, t)}
            userScores={topUserScores}
            showDepartment={true}
            competition={competition}
          />
        }
        <ScoreboardPointsTrend chartView={chartViewForScoreboardTab(scoreboardView)} />
      </div>
    );
  }
}

const mapStateToProps = (state: State, _ownProps: Props): Props => {
  const competition = state.competition;
  const teamScores = (state.competitionEntityScores && state.competitionEntityScores.team) || [];
  return {
    topTeamScores: teamScores.filter((score) => {
      const entity = state.entities && state.entities[score.entityID];
      return entity && !isHiddenFromScoreboard(entity, competition);
    }),
    topUserScores: state.competitionTopUserScores,
    competitionID: competition.id,
    competition,
    competitionConfig: (competition && competition.config) || {}
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocalization('scoreboard')
)(Scoreboard) as React.ComponentClass;
